import { appStorage } from './betaStorage'
import { useEffect, useState } from 'react'
import type { YouthParticipantId } from './youthParticipants'

export type NixiProfileVariant = 'girl' | 'boy'
export type NixiProfileColour = 'lavender' | 'pink' | 'mint'

export type NixiProfilePreferences = {
  variant: NixiProfileVariant
  colour: NixiProfileColour
}

const KEY_PREFIX = 'marnixia.nixi.preferences.v1.'
const CHANGE_EVENT = 'marnixia:nixi-preferences-change'

const defaults: Record<YouthParticipantId, NixiProfilePreferences> = {
  'u-leo': { variant: 'boy', colour: 'lavender' },
  'u-jnr': { variant: 'girl', colour: 'lavender' },
}

function keyFor(participantId: YouthParticipantId) {
  return KEY_PREFIX + participantId
}

export function loadNixiProfilePreferences(participantId: YouthParticipantId): NixiProfilePreferences {
  const fallback = defaults[participantId]
  try {
    const saved = JSON.parse(appStorage.getItem(keyFor(participantId)) || '{}') as Partial<NixiProfilePreferences>
    return {
      variant: saved.variant === 'girl' || saved.variant === 'boy' ? saved.variant : fallback.variant,
      colour: saved.colour === 'pink' || saved.colour === 'mint' || saved.colour === 'lavender' ? saved.colour : fallback.colour,
    }
  } catch {
    return fallback
  }
}

export function saveNixiProfilePreferences(participantId: YouthParticipantId, preferences: NixiProfilePreferences) {
  appStorage.setItem(keyFor(participantId), JSON.stringify(preferences))
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { participantId } }))
}

export function useNixiProfilePreferences(participantId: YouthParticipantId) {
  const [preferences, setPreferences] = useState(() => loadNixiProfilePreferences(participantId))

  useEffect(() => {
    setPreferences(loadNixiProfilePreferences(participantId))
    const refresh = (event: Event) => {
      const detail = event instanceof CustomEvent ? event.detail as { participantId?: string } : undefined
      if (!detail?.participantId || detail.participantId === participantId) {
        setPreferences(loadNixiProfilePreferences(participantId))
      }
    }
    window.addEventListener(CHANGE_EVENT, refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [participantId])

  const update = (next: Partial<NixiProfilePreferences>) => {
    const value = { ...preferences, ...next }
    saveNixiProfilePreferences(participantId, value)
    setPreferences(value)
  }

  return { preferences, update }
}


